import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';
import { AlertCircle, Copy, Plus, Trash2, CheckCircle } from 'lucide-react';

const Settings = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedConfig, setExpandedConfig] = useState(null);
  const [webhookToken, setWebhookToken] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    phoneNumberId: '',
    businessAccountId: '',
    accessToken: '',
    displayName: '',
    phoneNumber: '',
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await settingsAPI.getConfigurations();
      setConfigs(response.data.configs);
    } catch (err) {
      setError('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddConfig = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!formData.phoneNumberId || !formData.businessAccountId || !formData.accessToken) {
      setError('All fields are required');
      return;
    }

    try {
      await settingsAPI.addWhatsAppConfig(formData);
      setSuccessMessage('Configuration added successfully!');
      setFormData({
        phoneNumberId: '',
        businessAccountId: '',
        accessToken: '',
        displayName: '',
        phoneNumber: '',
      });
      setShowForm(false);
      fetchConfigs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add configuration');
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) return;

    try {
      await settingsAPI.deleteConfiguration(configId);
      setSuccessMessage('Configuration deleted successfully');
      fetchConfigs();
    } catch (err) {
      setError('Failed to delete configuration');
    }
  };

  const handleGetWebhookToken = async (configId) => {
    try {
      const response = await settingsAPI.getWebhookToken(configId);
      setWebhookToken(response.data);
      setExpandedConfig(configId);
    } catch (err) {
      setError('Failed to fetch webhook token');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage('Copied to clipboard!');
    setTimeout(() => setSuccessMessage(''), 2000);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Settings</h1>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded flex gap-3">
          <AlertCircle className="text-red-600" size={20} />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded flex gap-3">
          <CheckCircle className="text-green-600" size={20} />
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {/* Add New Config Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Add WhatsApp Configuration</h2>
          <form onSubmit={handleAddConfig} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number ID *
                </label>
                <input
                  type="text"
                  value={formData.phoneNumberId}
                  onChange={(e) => setFormData(prev => ({ ...prev, phoneNumberId: e.target.value }))}
                  placeholder="Your phone number ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Account ID *
                </label>
                <input
                  type="text"
                  value={formData.businessAccountId}
                  onChange={(e) => setFormData(prev => ({ ...prev, businessAccountId: e.target.value }))}
                  placeholder="Your business account ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                  placeholder="Name for this config"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  placeholder="+1234567890"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Access Token *
              </label>
              <textarea
                value={formData.accessToken}
                onChange={(e) => setFormData(prev => ({ ...prev, accessToken: e.target.value }))}
                placeholder="Your WhatsApp API access token"
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
              >
                Add Configuration
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="bg-gray-300 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Existing Configs */}
      <div className="mb-6">
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2 mb-6"
        >
          <Plus size={20} />
          Add WhatsApp Configuration
        </button>
      </div>

      <div className="space-y-4">
        {configs.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-600">No configurations added yet</p>
          </div>
        ) : (
          configs.map((config) => (
            <div key={config.id} className="bg-white rounded-lg shadow overflow-hidden">
              <div
                className="p-6 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedConfig(expandedConfig === config.id ? null : config.id)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {config.displayName || config.phoneNumber || 'WhatsApp Config'}
                    </h3>
                    <p className="text-gray-600 text-sm">Phone ID: {config.phoneNumberId}</p>
                    <p className="text-gray-600 text-sm">Business ID: {config.businessAccountId}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    config.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {config.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {expandedConfig === config.id && (
                <div className="border-t border-gray-200 p-6 bg-gray-50">
                  <div className="space-y-4 mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Webhook URL</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-white border border-gray-300 px-3 py-2 rounded text-sm text-gray-900 break-all">
                          {webhookToken?.webhookUrl || `${window.location.origin}/api/webhook`}
                        </code>
                        <button
                          onClick={() => copyToClipboard(webhookToken?.webhookUrl || `${window.location.origin}/api/webhook`)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <Copy size={20} />
                        </button>
                      </div>
                    </div>

                    {webhookToken && expandedConfig === config.id && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Webhook Verify Token</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-white border border-gray-300 px-3 py-2 rounded text-sm text-gray-900 break-all">
                            {webhookToken.webhookVerifyToken}
                          </code>
                          <button
                            onClick={() => copyToClipboard(webhookToken.webhookVerifyToken)}
                            className="text-green-600 hover:text-green-700"
                          >
                            <Copy size={20} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => handleGetWebhookToken(config.id)}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                    >
                      Show Webhook Token
                    </button>
                    <button
                      onClick={() => handleDeleteConfig(config.id)}
                      className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center gap-2 text-sm"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Integration Instructions */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-blue-900 mb-4">Integration Instructions</h3>
        <ol className="space-y-2 text-blue-900 text-sm list-decimal list-inside">
          <li>Go to your Meta Business App</li>
          <li>Get your Phone Number ID, Business Account ID, and Access Token</li>
          <li>Add the configuration here</li>
          <li>Configure the webhook URL in your Meta Business App</li>
          <li>Use the webhook verify token provided above</li>
        </ol>
      </div>
    </div>
  );
};

export default Settings;
